import React from 'react';
import {
  AsyncContentBoundary,
  ChatWorkspaceConversationPane,
  ChatWorkspaceProjectInitView,
  ChatWorkspaceSourcesPanel,
  FlareLogoMark,
  ProjectCollectionPane,
} from '../../../index.js';
import WorkspaceLoadingSkeleton from '../../../feedback/WorkspaceLoadingSkeleton.jsx';
import ProjectManagementPane from '../../../workspace/components/ProjectManagementPane.jsx';

function buildTabItems(labels) {
  return [
    { label: labels.tab_chats || '会话', value: 'chats' },
    { label: labels.tab_sources || '资料', value: 'sources' },
  ];
}

function shouldShowCompactTabs({ hasProject, isCompactLayout, renderedTimelineItems }) {
  return [hasProject, isCompactLayout, renderedTimelineItems.length === 0].every(Boolean);
}

function resolveProjectWorkspace({
  activeWorkspaceTab, collectionNode, directoryNode,
  initNode, workspaceSurface, wrapWorkspaceSurface,
}) {
  const workspacePending = [workspaceSurface === 'pending', activeWorkspaceTab !== 'projects'].every(Boolean);
  if (workspacePending) {
    return wrapWorkspaceSurface(null);
  }
  if (workspaceSurface === 'project_init' && activeWorkspaceTab !== 'projects') {
    return wrapWorkspaceSurface(initNode);
  }
  if (workspaceSurface === 'project_directory' || activeWorkspaceTab === 'projects') {
    return wrapWorkspaceSurface(directoryNode);
  }
  if (workspaceSurface === 'project_collection') return wrapWorkspaceSurface(collectionNode);
  if (workspaceSurface === 'conversation_draft') return undefined;
  return undefined;
}

export default function ChatBodySection(inputProps) {
  const {
  hasProject,
  activeSessionId,
  workspaceSuspension,
  workspaceSurface,
  activeWorkspaceTab,
  setActiveWorkspaceTab,
  themeTokens,
  projectItems,
  collectionScopeKey,
  projectSlot,
  resolvedProductName,
  resolvedUILabels,
  handleCreateProject,
  createProjectDisabled,
  composerNode,
  contentMaxWidth,
  handleOpenSourcePicker,
  handleRemoveSource,
  handleRetrySource,
  handleViewSourceDetail,
  sourceActionError,
  sourceItems,
  sourceRemovingId,
  sourceSyncLoading,
  sourceUploadLoading,
  sessions,
  sessionError,
  sessionLoading,
  sessionLoadingMore,
  sessionHasMore,
  sessionContinuationKey,
  sessionSortOrder,
  projectHasMore,
  projectContinuationKey,
  projectLoading,
  projectLoadingMore,
  loadingSessionId,
  runningSessionIds,
  onSelectSession,
  onRenameSession,
  onArchiveSession,
  onArchiveProject,
  onPinProject,
  onPinSession,
  onLoadMoreSessions,
  onLoadMoreProjects,
  onRetrySessions,
  onSortSessions,
  onSearchProjects,
  projectSearch,
  onProjectSelect,
  onRenameProject,
  artifactCollection,
  skillProjection,
  showCanvasPanel,
  canvasFullscreenOpen,
  handleCloseWorkspacePanel,
  canvasPanelNode,
  streamError,
  streamLoading,
  messageWindow,
  onLoadOlderMessages,
  onStreamRetry,
  renderedTimelineItems,
  onUICardAction,
  onWorkflowOperationAction,
  registry,
  currentInputValue,
  canExpandScenarios,
  showAllScenarios,
  onToggleShowAllScenarios,
  visibleScenarios,
  shouldRenderRecommendationPanel,
  recommendationPanelTitle,
  onScenarioCardClick,
  isCompactLayout,
  } = {
    workspaceSuspension: { active: false, label: '', operationKey: '', requestId: 0 },
    workspaceSurface: 'pending',
    resolvedProductName: 'F.L.A.R.E',
    resolvedProductTag: '项目协同工作台',
    createProjectDisabled: false,
    canExpandScenarios: false,
    shouldRenderRecommendationPanel: true,
    ...inputProps,
  };
  const sourcesPanelProps = {
    composerNode, contentMaxWidth, handleOpenSourcePicker, handleRemoveSource,
    handleRetrySource, handleViewSourceDetail, isCompactLayout, resolvedUILabels,
    sourceActionError, sourceItems, sourceRemovingId, sourceSyncLoading,
    sourceUploadLoading, themeTokens,
  };
  // Canonical read failures are rendered by the boundary, not left as unhandled click promises.
  const handleSelectSession = React.useCallback(sessionId => (
    Promise.resolve().then(() => onSelectSession?.(sessionId)).catch(() => {})
  ), [onSelectSession]);
  const tabItems = buildTabItems(resolvedUILabels);
  const activeTabIndex = Math.max(
    0,
    tabItems.findIndex((item) => item.value === activeWorkspaceTab),
  );
  const compactTabSwitchNode = shouldShowCompactTabs({ hasProject, isCompactLayout, renderedTimelineItems }) ? (
    <div className="flare-workspace-compact-tabs" style={{ padding: '8px 12px 6px' }}>
      <div
        className="flare-workspace-tab-switch"
        role="tablist"
        aria-label="workspace tabs"
      >
        <span
          className="flare-workspace-tab-switch-thumb"
          style={{ transform: `translateX(${activeTabIndex * 100}%)` }}
        />
        {tabItems.map((item) => {
          const active = item.value === activeWorkspaceTab;
          return (
            <button
              key={item.value}
              aria-selected={active}
              className={`flare-workspace-tab-switch-item${active ? ' is-active' : ''}`}
              onClick={() => setActiveWorkspaceTab(item.value)}
              role="tab"
              type="button"
            >
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  ) : null;
  const wrapWorkspaceSurface = (content) => (
    <AsyncContentBoundary
      heading={resolvedProductName}
      indicator={<FlareLogoMark compact themeTokens={themeTokens} />}
      fallback={<WorkspaceLoadingSkeleton contentMaxWidth={contentMaxWidth} />}
      projection={activeWorkspaceTab === 'projects' ? undefined : workspaceSuspension}
      style={{ background: themeTokens.panelBg }}
    >
      {content}
    </AsyncContentBoundary>
  );
  const handleCreateProjectAndEnter = async (options) => {
    setActiveWorkspaceTab?.('chats');
    await handleCreateProject?.(options);
  };
  const handleProjectSelectAndEnter = async (project) => {
    await onProjectSelect?.(project);
    setActiveWorkspaceTab?.('chats');
  };
  const projectWorkspace = resolveProjectWorkspace({
    activeWorkspaceTab,
    initNode: (
      <ChatWorkspaceProjectInitView
        contentMaxWidth={contentMaxWidth}
        collectionScopeKey={collectionScopeKey}
        createProjectDisabled={createProjectDisabled}
        handleCreateProject={handleCreateProjectAndEnter}
        isCompactLayout={isCompactLayout}
        resolvedProductName={resolvedProductName}
        resolvedUILabels={resolvedUILabels}
        themeTokens={themeTokens}
      />
    ),
    directoryNode: (
      <ProjectManagementPane
        onPinProject={onPinProject}
        projectSearch={projectSearch}
        contentMaxWidth={1120}
        createProjectDisabled={createProjectDisabled}
        createProjectLabel={resolvedUILabels.new_project_button}
        error={sessionError}
        onCreateProject={handleCreateProjectAndEnter}
        onLoadMoreProjects={onLoadMoreProjects}
        onProjectSelect={handleProjectSelectAndEnter}
        onRetry={onRetrySessions}
        onSearchProjects={onSearchProjects}
        projectHasMore={projectHasMore}
        projectContinuationKey={projectContinuationKey}
        projectItems={projectItems}
        projectLoading={projectLoading}
        projectLoadingMore={projectLoadingMore}
        projectManagementLabel={resolvedUILabels.project_management}
        themeTokens={themeTokens}
      />
    ),
    collectionNode: (
      <ProjectCollectionPane
        projectSearch={projectSearch}
        activeWorkspaceTab={activeWorkspaceTab}
        onArchiveProject={onArchiveProject}
        onPinProject={onPinProject}
        onRenameProject={onRenameProject}
        onWorkspaceTabChange={setActiveWorkspaceTab}
        sourcesPanelProps={sourcesPanelProps}
        artifactCollection={artifactCollection}
        canvasPanelNode={canvasPanelNode}
        canvasWorkspaceFullscreenMode={canvasFullscreenOpen === true}
        onCanvasPanelClose={handleCloseWorkspacePanel}
        showCanvasPanel={showCanvasPanel}
        composerNode={composerNode}
        contentMaxWidth={1120}
        isCompactLayout={isCompactLayout}
        project={projectSlot}
        resolvedUILabels={resolvedUILabels}
        sessionCollection={{
          activeSessionId,
          activeSessionRunning: streamLoading,
          error: sessionError,
          hasMore: sessionHasMore,
          continuationKey: sessionContinuationKey,
          loading: sessionLoading,
          loadingMore: sessionLoadingMore,
          loadingSessionId,
          onArchiveSession,
          onPinSession,
          onLoadMore: onLoadMoreSessions,
          onRenameSession,
          onRetry: onRetrySessions,
          onSelectSession: handleSelectSession,
          onSortSessions,
          runningSessionIds,
          sessions,
          sortOrder: sessionSortOrder,
        }}
        themeTokens={themeTokens}
      />
    ),
    workspaceSurface,
    wrapWorkspaceSurface,
  });
  if (projectWorkspace !== undefined) {
    return projectWorkspace;
  }
  if (activeWorkspaceTab === 'sources') {
    return wrapWorkspaceSurface(
      <>
        {compactTabSwitchNode}
        <ChatWorkspaceSourcesPanel {...sourcesPanelProps} />
      </>
    );
  }

  return wrapWorkspaceSurface(
    <>
      {compactTabSwitchNode}
      <ChatWorkspaceConversationPane
        key={activeSessionId}
        assistantName={resolvedProductName}
        themeTokens={themeTokens}
        isCompactLayout={isCompactLayout}
        resolvedActiveModeKey="auto"
        canvasWorkspaceFullscreenMode={canvasFullscreenOpen === true}
        showCanvasPanel={showCanvasPanel}
        onCanvasPanelClose={handleCloseWorkspacePanel}
        canvasPanelNode={canvasPanelNode}
        composerNode={composerNode}
        contentMaxWidth={Math.min(contentMaxWidth, 780)}
        streamError={streamError}
        streamLoading={streamLoading}
        messageWindow={messageWindow}
        onLoadOlderMessages={onLoadOlderMessages}
        onStreamRetry={onStreamRetry}
        renderedTimeline={{
          isEmpty: renderedTimelineItems.length === 0,
          items: renderedTimelineItems,
        }}
        onUICardAction={onUICardAction}
        onWorkflowOperationAction={onWorkflowOperationAction}
        registry={registry}
        skillProjection={skillProjection}
        resolvedUILabels={resolvedUILabels}
        showWorkspaceDebugPanel={false}
        resolvedDevMode="full_effects"
        workspaceEventLog={[]}
        workspaceDocSnapshot={{}}
        shouldRenderRecommendationPanel={shouldRenderRecommendationPanel}
        currentInputValue={currentInputValue}
        recommendationPanelTitle={recommendationPanelTitle}
        canExpandScenarios={canExpandScenarios}
        showAllScenarios={showAllScenarios}
        onToggleShowAllScenarios={onToggleShowAllScenarios}
        visibleScenarios={visibleScenarios}
        onScenarioCardClick={onScenarioCardClick}
      />
    </>,
  );
}
